import EventEmitter from "event-lite";

import { IS_APP, IS_NODE } from "@/__main__/constants.mjs";
import { readData } from "@/__main__/get-data.mjs";
import mainRefs from "@/__main__/refs.mjs";
import type {
  ActiveRoute,
  ComponentModule,
  RouteState,
} from "@/__main__/router.mjs";
import { initSettings, overrideFeatureFlagsFromCMS } from "@/app/actions.mjs";
import gamesList from "@/app/games.mjs";
import initAppShell from "@/app/init-app-shell.mjs";
import { syncAppVersion } from "@/hub-settings/actions.mjs";
import { catchAllRoutes } from "@/routes/constants.mjs";
import gameRoutes from "@/routes/game.mjs";
import generalRoutes, { gameHubRoutes } from "@/routes/general.mjs";
import {
  appRoutes as appMarketingRoutes,
  standaloneRoutes as standaloneMarketingRoutes,
} from "@/routes/marketing.mjs";
import overlayRoutesPerGame from "@/routes/overlay.mjs";
import routesRefs from "@/routes/routes.refs.mjs";
import { IS_SEARCH_ACTIVE, SEARCH_QUERY } from "@/search/constants.mjs";
import { hasMarkup } from "@/util/constants.mjs";
import { devError } from "@/util/dev.mjs";
import { initReferrerInfo } from "@/util/download-app.mjs";
import globals from "@/util/global-whitelist.mjs";
import isRouteOverlay from "@/util/is-route-overlay.mjs";
import lazyFn from "@/util/lazy-fn.mjs";
import type { RouteRegExp } from "@/util/path-regexp.mjs";

const fetchNotifications = lazyFn(
  () => import("@/app/fetch-notifications.mjs"),
);

const fetchReleaseNotes = lazyFn(() => import("@/app/fetch-release-notes.mjs"));

const fetchSearchData = lazyFn(() => import("@/search/fetch-search-data.mjs"));
const fetchSearchPlayers = lazyFn(
  () => import("@/search/fetch-search-players.mjs"),
);

type RedirectResult = string | boolean | undefined;

export interface BaseRoute {
  path?: string | RouteRegExp;

  redirect?:
    | string
    | ((
        route?: ActiveRoute,
        search?: URLSearchParams,
        state?: RouteState,
      ) => RedirectResult | Promise<RedirectResult>);

  fetchData?: (
    params: Array<string>,
    search: URLSearchParams,
    state: RouteState,
  ) => Promise<unknown> | unknown;

  component?: string | ComponentModule | (() => Promise<ComponentModule>);

  hubSymbol?: symbol;

  key?: string;
  placeholder?: boolean;
  skipTesting?: boolean;

  [catchAllRoutes]?: boolean | BaseRoute[];
}

// Merge all game-related routes into gameRoutes.
for (const { gameSymbol } of gamesList.filter((i) => i.isActive)) {
  if (overlayRoutesPerGame[gameSymbol])
    gameRoutes[gameSymbol].push(...overlayRoutesPerGame[gameSymbol]);
  gameRoutes[gameSymbol].push(...gameHubRoutes[gameSymbol]);
}

// These are routes which do not belong to any game, but should still
// show app navigation.
export const appRoutes: BaseRoute[] = [...generalRoutes, ...appMarketingRoutes];

const defaultRouteEmitter = new EventEmitter();
const initializeDefaultRoute = new Promise((resolve) => {
  defaultRouteEmitter.once("didInitDefaultRoute", resolve);
});
mainRefs.initializeDefaultRoute = initializeDefaultRoute;

// The default route should fetch global data (user, settings).
// It also has a special behavior in the router if these conditions are met:
// 1) It is the first initial render
// 2) There is SSR content available
// Then it should defer calling `fetchData` until after rendering.
// This is to guarantee that there's no mismatch between server/client render.
// https://www.benmvp.com/blog/handling-react-server-mismatch-error/
export const defaultRoute: BaseRoute = {
  path: /.*/,
  [catchAllRoutes]: true,
  // This is the default route for the entire app, please be very careful about
  // not blocking anything here. In particular, network requests should not
  // block here.
  fetchData(_pathArr, _searchParams, state) {
    // Please read the comment above *again*, be very careful about whether a
    // request should be blocking here or not, and consider if it's required or
    // a side effect.

    const shouldInitClientOnly =
      !IS_NODE && (!state.isInitializing || !hasMarkup);

    // ========================================================
    // WARNING: CHOOSING THE WRONG ARRAY HERE CAN DE-OPTIMIZE
    // ALL ROUTES.
    const requiredPromises: (void | Promise<unknown>)[] = [
      shouldInitClientOnly && initSettings(),
      shouldInitClientOnly && !state.isUpdate && readData(["localState"]),
      initAppShell().then(() => {
        defaultRouteEmitter.emit("didInitDefaultRoute");
      }),
    ];
    const sideEffectPromises: (void | Promise<unknown>)[] = [
      overrideFeatureFlagsFromCMS(),
      !IS_APP && !IS_NODE
        ? (initReferrerInfo() as unknown as Promise<unknown>)
        : null,
    ];
    // ========================================================

    const pathname = globals.location?.pathname;
    if (IS_APP) sideEffectPromises.push(syncAppVersion());
    if (!isRouteOverlay(pathname)) {
      sideEffectPromises.push(
        readData(["marketing", "newNavItems"], { initializeOnce: true }),
        ...(!IS_NODE
          ? routesRefs.defaultRouteSideEffects.map((fn) => fn())
          : []),
      );
      requiredPromises.push(
        ...routesRefs.defaultRouteFetchFunctions.map((fn) => fn()),
      );

      if (IS_APP) {
        sideEffectPromises.push(fetchNotifications(), fetchReleaseNotes());
      }
    }

    if (state[IS_SEARCH_ACTIVE]) {
      requiredPromises.push(fetchSearchData());
    }

    if (state[SEARCH_QUERY]) {
      sideEffectPromises.push(fetchSearchPlayers(state));
    }

    Promise.all(sideEffectPromises).catch((error) => {
      devError("DEFAULT ROUTE SIDE EFFECT", error);
    });

    return Promise.all(requiredPromises).catch((error) => {
      devError("DEFAULT ROUTE FAILURE", error);
    });
  },
};

const routes: BaseRoute[] = [
  defaultRoute,
  ...appRoutes,
  ...standaloneMarketingRoutes,
  ...Object.getOwnPropertySymbols(gameRoutes).reduce((a, gameSymbol) => {
    a.push(...gameRoutes[gameSymbol]);
    return a;
  }, []),
];

mainRefs.routes = routes;
mainRefs.defaultRoute = defaultRoute;

// You should probably not import the default export because it is just a list
// of all routes with no meta-info on the routes.
export default routes;
